import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { BookButton } from "./BookButton";
import { OpenNowBadge } from "./OpenNowBadge";
import { DesktopNav } from "./nav/DesktopNav";
import { MobileMenu } from "./nav/MobileMenu";
import { useSite } from "@/context/SiteContext";
import { api, resolveImg } from "@/lib/api";

// Static menu structure. "Where Do You Need Relief?" and "Treatments" are populated live from the CMS.
const STATIC_NAV = [
  {
    label: "Our Approach",
    children: [
      { to: "/approach", label: "Treat the Chain · Body Map" },
      { to: "/nervous-system", label: "Nervous System" },
      { to: "/nervous-system", label: "Top Down / Bottom Up" },
      { to: "/first-visit", label: "Your First Visit" },
    ],
  },
  {
    label: "Care",
    children: [
      { to: "/pregnancy", label: "Pregnancy" },
      { to: "/pediatric", label: "Pediatrics" },
      { to: "/family-care", label: "Whole Family" },
    ],
  },
  { label: "Where Do You Need Relief?", dynamic: "concerns", viewAll: { to: "/conditions", label: "View All Concerns" } },
  { label: "Treatments", dynamic: "services", viewAll: { to: "/treatments", label: "View All Treatments" } },
  {
    label: "Patients",
    children: [
      { to: "/first-visit", label: "New Patients" },
      { to: "/portal", label: "Patient Portal" },
      { to: "/memberships", label: "Memberships" },
      { to: "/portal", label: "Rewards" },
      { to: "/patient-stories", label: "Patient Stories" },
      { to: "/learn", label: "Learn / Resources" },
    ],
  },
  {
    label: "About",
    children: [
      { to: "/about", label: "Our Story" },
      { to: "/reviews", label: "Google Reviews" },
      { to: "/approach", label: "Our Philosophy" },
      { to: "/contact", label: "Contact" },
      { to: "/contact", label: "Hours & Location" },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

export function Nav() {
  const location = useLocation();
  const overHero = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [concerns, setConcerns] = useState([]);
  const [services, setServices] = useState([]);
  const { settings } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setOpenSection(null); }, [location.pathname]);

  useEffect(() => {
    api.get("/concerns").then((r) => setConcerns(r.data || [])).catch(() => {});
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  // Build the full menu, injecting live concern/treatment children.
  const NAV = STATIC_NAV.map((item) => {
    if (item.dynamic === "concerns") {
      return { ...item, children: concerns.slice(0, 8).map((c) => ({ to: `/conditions/${c.slug}`, label: c.name })) };
    }
    if (item.dynamic === "services") {
      return { ...item, children: services.slice(0, 8).map((s) => ({ to: `/treatments/${s.slug}`, label: s.name })) };
    }
    return item;
  });

  const solid = scrolled || !overHero;
  const textColor = solid ? "text-espresso" : "text-ivory";

  let logoSrc = null;
  let logoInvert = false;
  if (solid) {
    logoSrc = settings?.logo_url || null;
  } else if (settings?.logo_light_url) {
    logoSrc = settings.logo_light_url;
  } else if (settings?.logo_url) {
    logoSrc = settings.logo_url;
    logoInvert = true;
  }

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solid ? "bg-ivory shadow-[0_1px_0_rgba(42,36,33,0.06)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-20 lg:h-24">
          <Link to="/" data-testid="nav-logo" className={`flex items-center leading-none ${textColor} transition-colors`}>
            {logoSrc ? (
              <img src={resolveImg(logoSrc)} alt="Psalm 30:2 Chiropractic" className="h-14 lg:h-16 w-auto object-contain" style={{ filter: logoInvert ? "invert(1)" : "none" }} />
            ) : (
              <span className="flex flex-col">
                <span className="font-serif text-2xl lg:text-3xl tracking-tight">Psalm 30:2</span>
                <span className="text-[0.6rem] uppercase tracking-[0.35em] mt-1 opacity-80">Chiropractic</span>
              </span>
            )}
          </Link>

          <DesktopNav nav={NAV} textColor={textColor} slug={slug} />

          <div className="flex items-center gap-4">
            <div className="hidden 2xl:block"><OpenNowBadge light={!solid} /></div>
            <div className="hidden sm:block">
              <BookButton label="Book a Visit" variant={solid ? "primary" : "light"} track_label="nav-desktop" testid="nav-book-visit-button" className="!px-6 !py-3" />
            </div>
            <button data-testid="nav-mobile-toggle" onClick={() => setMenuOpen(true)} className={`xl:hidden ${textColor}`} aria-label="Open menu">
              <Menu size={26} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        nav={NAV}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        openSection={openSection}
        setOpenSection={setOpenSection}
        slug={slug}
      />
    </>
  );
}
